// external
import { useCallback, useContext } from "react";
import { toast } from "react-toastify";

// api
import apiRoot from "api/apiRoot";

// state managing
import { FaoBuilderContext } from "context/FaoBuilderContext";

function useModelRefresh() {
  const { modelConfig, refreshDispatch, refreshStatus, pollJobRunStatus } =
    useContext(FaoBuilderContext);

  const getRefreshJobStatus = useCallback(
    async (runId) => {
      const currentToast = refreshStatus[modelConfig.id]?.toastId;

      const response = await pollJobRunStatus(runId);
      toast.dismiss(currentToast);
      if (response.success) {
        toast.success("SUCCESS - Dataset is refreshed");
      } else {
        toast.error(response.message);
      }
      refreshDispatch({
        type: "refresh_complete",
        modelId: modelConfig.id,
      });
    },
    [pollJobRunStatus, refreshDispatch, modelConfig.id, refreshStatus]
  );

  /*
   * handles refresh of models - triggering a job in the backend
   */
  const handleRefresh = useCallback(
    async (endpoint) => {
      // if import config doesn't exist for the model then return an error
      if (
        !modelConfig.configs.importConfig &&
        endpoint === "/api/fao-imports/refresh"
      ) {
        toast.error("Import must be saved before refreshing.");
        return;
      }
      // if there is an active refresh for the model, then return an error
      if (refreshStatus[modelConfig.id]?.isRefreshing) {
        if (endpoint === "/api/fao-imports/refresh") {
          toast.error(
            "Found an active job for model: " +
              modelConfig.id +
              ". Please try again later."
          );
        }
        return;
      }
      try {
        // api call to the backend to trigger a job
        const response = await apiRoot.post(
          endpoint,
          {},
          {
            params: { modelId: modelConfig.id },
          }
        );
        if (response.data.runStatus === "SUCCESS") {
          return;
        }
        // if the call didn't trigger a job, show an error message and mark the refresh as complete
        if (response.data.runStatus === "FAILURE") {
          toast.error(response.data.message, {
            autoClose: false,
          });
        }
        if (response.data.runStatus === "RUNNING") {
          // start a loading toast
          const toastId = toast.loading("Refreshing dataset...", {
            autoClose: false,
          });
          refreshDispatch({
            type: "refresh_found",
            modelId: modelConfig.id,
            toastId,
          });
          getRefreshJobStatus(response.data.runId);
        }
      } catch (error) {
        console.log(error);
        const currentToast = refreshStatus[modelConfig.id]?.toastId;
        if (currentToast) {
          toast.dismiss(currentToast);
        }
        toast.error("Error with dataset refresh", {
          autoClose: false,
        });
      }
    },
    [modelConfig, refreshStatus, getRefreshJobStatus, refreshDispatch]
  );
  return {
    handleRefresh,
    pollJobRunStatus,
  };
}

export default useModelRefresh;
